/**
 * DrilldownGrid — the records behind a KPI. Paged, sortable, searchable, with
 * a column chooser and CSV export. Scoped by the shared DashboardContext
 * (site/time/department) so it always matches the KPI it drills into.
 *
 * Server does the paging/sorting/searching (/api/dashboard/v2/drilldown); this
 * is a thin, reusable table over that contract.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routeBuilderFor, recordLinkId } from '../utils/recordRoutes';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';
import { Search, ArrowUp, ArrowDown, Download, Columns3, Loader2 } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { useDashboard } from '../context/DashboardContext';
import { fetchDrilldown } from '../api/client';

const PAGE_SIZE = 25;


function formatCell(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function toCsv(columns, rows) {
  const header = columns.map((c) => c.label).join(',');
  const body = rows
    .map((r) => columns.map((c) => {
      const v = r[c.key];
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    }).join(','))
    .join('\n');
  return `${header}\n${body}`;
}

export default function DrilldownGrid({
  metric, title, extraDimensions = null, siteOverride = null, timeOverride = null,
}) {
  const navigate = useNavigate();
  const { siteId, time: ctxTime, departmentId, dimensions, fyStartMonth } = useDashboard();
  const effectiveSite = siteOverride || siteId;
  // A trend-point drill narrows to ONE period (e.g. the March a user clicked),
  // which is a different range from the dashboard's own filter — hence an
  // override rather than reading context.
  const time = timeOverride || ctxTime;
  // Merge context filters with any extra (e.g. a bar-click "department=X").
  const mergedDims = extraDimensions
    ? { ...(dimensions || {}), ...extraDimensions }
    : (dimensions || {});
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = JSON.stringify(mergedDims);

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState(() => new Set());
  const [showCols, setShowCols] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Debounce the search box.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset to page 1 whenever the query shape changes.
  useEffect(() => setPage(1), [metric, siteId, timeKey, departmentId, sort, sortDir, search]);

  useEffect(() => {
    if (!metric) return undefined;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    const body = {
      metric, page, page_size: PAGE_SIZE, sort, sort_dir: sortDir,
    };
    if (effectiveSite) body.site_id = effectiveSite;
    if (departmentId) body.department_id = departmentId;
    if (Object.keys(mergedDims).length) body.dimensions = mergedDims;
    if (time) body.time = time;
    if (fyStartMonth) body.fy_start_month = fyStartMonth;
    if (search) body.search = search;
    fetchDrilldown(body, { signal: ctrl.signal })
      .then((resp) => { if (abortRef.current === ctrl) setData(resp); })
      .catch((e) => {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED' && abortRef.current === ctrl) setError(e);
      })
      .finally(() => { if (abortRef.current === ctrl) setLoading(false); });
    return () => ctrl.abort();
  }, [metric, effectiveSite, timeKey, dimsKey, departmentId, page, sort, sortDir, search, fyStartMonth]);

  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const routeFor = routeBuilderFor(data);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const visibleCols = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);

  const onSort = (key) => {
    if (sort === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setSortDir('desc'); }
  };

  const toggleCol = (key) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const exportCsv = () => {
    const blob = new Blob([toCsv(visibleCols, rows)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || metric || 'records').replace(/\W+/g, '_')}_p${page}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search…"
            className="rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCols((s) => !s)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Columns3 className="h-4 w-4" /> Columns
            </button>
            {showCols && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-md border border-gray-200 bg-white p-2 shadow-lg">
                {columns.map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50">
                    <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={!rows.length}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleCols.map((c) => (
                <TableHead key={c.key}>
                  <button type="button" onClick={() => onSort(c.key)} className="inline-flex items-center gap-1 font-medium hover:text-gray-900">
                    {c.label}
                    {sort === c.key && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={visibleCols.length || 1} className="py-8 text-center text-gray-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={visibleCols.length || 1} className="py-8 text-center">
                  {isSiteRequiredError(error)
                    ? <SiteRequiredNotice />
                    : <span className="text-red-600">Failed to load records</span>}
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleCols.length || 1} className="py-8 text-center text-gray-400">No records</TableCell>
              </TableRow>
            )}
            {!loading && !error && rows.map((row, i) => {
              // No link id means no destination — the row stays inert rather
              // than navigating somewhere that cannot load. A hazard whose
              // risk_assessment_id is null is exactly that case.
              const linkId = recordLinkId(data, row);
              const clickable = routeFor && linkId;
              return (
                <TableRow
                  key={row.id || i}
                  className={clickable ? 'cursor-pointer hover:bg-blue-50' : undefined}
                  onClick={clickable ? () => navigate(routeFor(linkId, row)) : undefined}
                  title={clickable ? 'Open record' : undefined}
                >
                  {visibleCols.map((c) => (
                    <TableCell key={c.key}>{formatCell(row[c.key])}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
        <span>{total.toLocaleString()} record{total === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );
}
